// index.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ForwardRefRenderFunction
} from 'react';

// styles
import {
  StyledHeaderCell,
  StyledTable,
  StyledTableCell,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
  StyledTableWrapper
} from './style';

// components
import { Pagination } from 'components';
import { BasicCheckbox } from 'components/Checkboxs/BasicCheckbox';

// types
import type {
  BasicTableComponent,
  BasicTableProps,
  ColumnGroupType,
  ColumnType,
  HeaderCell,
  SortConfig,
  SortDirection,
  StyledTableCellProps
} from './types';

// libraries
import { KeyboardArrowRight } from '@mui/icons-material';
import { Paper, Radio, TableBody, TableRow, TableSortLabel } from '@mui/material';
import { uniqueId } from 'lodash-es';

const BasicTableBase: ForwardRefRenderFunction<HTMLDivElement, BasicTableProps<any>> = <
  T extends Record<string, any>
>(
  props: BasicTableProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const {
    columns,
    dataSource,
    noIndex = false,
    noTotal = false,
    rowKey,
    rowSelection,
    onRow,
    onHeaderRow,
    rowRef,
    rootClassName,
    className,
    headerClassName,
    bodyClassName,
    noPagenation = false,
    isAbsoluteToBottom = false,
    pagination = false,
    onChangePageSize,
    rowGrouping,
    infiniteScroll = false
  } = props;
  const scrollPositionRef = useRef<number>(0);
  const prevDataSourceLengthRef = useRef<number>(0);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: 'ascend'
  });

  const [selectedKeys, setSelectedKeys] = useState<(string | number)[]>(() => {
    const keys = rowSelection?.selectedRowKeys?.filter((item) => item);

    return keys || [];
  });

  const [expandedGroups, setExpandedGroups] = useState<Set<string | number>>(() => {
    const expanded = new Set<string | number>();

    if (rowGrouping?.defaultExpanded) {
      if (dataSource && rowGrouping.groupBy) {
        dataSource.forEach((record) => {
          const groupKey = rowGrouping.groupBy(record);
          expanded.add(groupKey);
        });
      }
    }
    return expanded;
  });

  const combinedRef = useCallback(
    (node: HTMLDivElement) => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }

      tableContainerRef.current = node;
    },
    [ref]
  );

  useEffect(() => {
    if (!infiniteScroll || !tableContainerRef.current) return;

    const handleScroll = () => {
      if (tableContainerRef.current) {
        scrollPositionRef.current = tableContainerRef.current.scrollTop;
      }
    };

    const container = tableContainerRef.current;
    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [infiniteScroll]);

  useEffect(() => {
    if (!infiniteScroll || !dataSource || !tableContainerRef.current) return;

    if (
      dataSource.length > prevDataSourceLengthRef.current &&
      prevDataSourceLengthRef.current > 0
    ) {
      setTimeout(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollTop = scrollPositionRef.current;
        }
      }, 0);
    }

    prevDataSourceLengthRef.current = dataSource.length;
  }, [dataSource, infiniteScroll]);

  useEffect(() => {
    if (rowSelection?.selectedRowKeys) {
      setSelectedKeys(rowSelection.selectedRowKeys);
    }
  }, [rowSelection?.selectedRowKeys]);

  const getRowKey = useCallback(
    (record: T, index: number): string | number => {
      if (typeof rowKey === 'function') {
        return rowKey(record);
      }

      if (typeof rowKey === 'string') {
        return record[rowKey] as string | number;
      }

      return record.key ?? uniqueId(String(index));
    },
    [rowKey]
  );

  const toggleGroupExpansion = useCallback((groupKey: string | number) => {
    setExpandedGroups((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(groupKey)) {
        newExpanded.delete(groupKey);
      } else {
        newExpanded.add(groupKey);
      }
      return newExpanded;
    });
  }, []);

  const _dataSource = useMemo(() => {
    return dataSource.map((item, idx) => {
      const rowKey = getRowKey(item, idx);

      return { ...item, key: rowKey || idx };
    });
  }, [dataSource, getRowKey]);

  const handleSort = (column: ColumnType<T>): void => {
    if (!column.sorter) return;

    const newDirection: SortDirection =
      sortConfig.key === column.dataIndex && sortConfig.direction === 'ascend'
        ? 'descend'
        : 'ascend';

    setSortConfig({
      key: String(column.dataIndex),
      direction: newDirection
    });
  };

  const getCellContent = (record: T, column: ColumnType<T>, index: number): React.ReactNode => {
    const value = record[column.dataIndex as keyof T];

    if (column.render) {
      return column.render(value, record, index);
    }

    return value;
  };

  const getCellProps = (
    column: ColumnType<T>,
    record: T,
    rowIndex: number
  ): React.HTMLAttributes<HTMLTableCellElement> & StyledTableCellProps => {
    const baseProps = {
      align: column.align || 'center',
      ellipsis: column.ellipsis,
      style: {
        width: column.width,
        minWidth: column.minWidth
      }
    };

    if (column.onCell) {
      return { ...baseProps, ...column.onCell(record, rowIndex) };
    }

    return baseProps;
  };

  const getRowProps = (record: T, index: number) => {
    const baseProps = onRow?.(record, index) || {};

    if (rowSelection) {
      const originalClick = baseProps.onClick;

      return {
        ...baseProps,
        onClick: (e: React.MouseEvent<HTMLTableRowElement>) => {
          e.stopPropagation();
          // handleRowSelection(record);
          originalClick?.(e);
        }
      };
    }

    return baseProps;
  };

  const getSortedData = useCallback((): T[] => {
    if (!sortConfig.key) return _dataSource;

    const column = columns.find((col) => col.dataIndex === sortConfig.key);
    if (!column) return _dataSource;

    return [..._dataSource].sort((a: T, b: T) => {
      if (typeof column.sorter === 'function') {
        return sortConfig.direction === 'ascend' ? column.sorter(a, b) : column.sorter(b, a);
      }

      const aValue = a[column.dataIndex as keyof T];
      const bValue = b[column.dataIndex as keyof T];

      if (sortConfig.direction === 'ascend') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });
  }, [_dataSource, columns, sortConfig.direction, sortConfig.key]);

  const handleRowSelection = useCallback(
    (record: T) => {
      if (!rowSelection) return;

      const key = record.key as string | number;
      const isSelected = selectedKeys.includes(key);
      let newSelectedKeys: (string | number)[];

      if (rowSelection.type === 'radio') {
        newSelectedKeys = isSelected ? [] : [key];
      } else {
        newSelectedKeys = isSelected
          ? selectedKeys.filter((k) => k !== key)
          : [...selectedKeys, key];
      }

      setSelectedKeys(newSelectedKeys);
      const selectedRows = _dataSource.filter((item) =>
        newSelectedKeys.includes(item.key as string | number)
      );
      rowSelection.onChange?.(newSelectedKeys, selectedRows);
    },
    [_dataSource, rowSelection, selectedKeys]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!rowSelection) return;

      const availableKeys = _dataSource
        .filter((record) => {
          const checkboxProps = rowSelection.getCheckboxProps?.(record) || {};
          return !checkboxProps.disabled;
        })
        .map((record) => record.key as string | number);

      const newSelectedKeys = checked ? availableKeys : [];
      setSelectedKeys(newSelectedKeys);

      const selectedRows = _dataSource.filter((item) =>
        newSelectedKeys.includes(item.key as string | number)
      );
      rowSelection.onChange?.(newSelectedKeys, selectedRows);
    },
    [_dataSource, rowSelection]
  );

  const isColumnGroup = <T extends Record<string, any>>(
    column: ColumnType<T> | ColumnGroupType<T>
  ): column is ColumnGroupType<T> => {
    return 'children' in column && Array.isArray(column.children);
  };

  const getLeafColumns = <T extends Record<string, any>>(
    columns: (ColumnType<T> | ColumnGroupType<T>)[]
  ): ColumnType<T>[] => {
    const leafColumns: ColumnType<T>[] = [];

    columns.forEach((column) => {
      if (isColumnGroup(column)) {
        leafColumns.push(...getLeafColumns(column.children));
      } else if (column.dataIndex) {
        leafColumns.push(column);
      }
    });

    return leafColumns;
  };

  const getHeaderRows = <T extends Record<string, any>>(
    columns: (ColumnType<T> | ColumnGroupType<T>)[]
  ): (ColumnType<T> | ColumnGroupType<T>)[][] => {
    const headerRows: (ColumnType<T> | ColumnGroupType<T>)[][] = [];

    const maxLevel = (columns: (ColumnType<T> | ColumnGroupType<T>)[], level = 0): number => {
      let maxDepth = level;
      columns.forEach((column) => {
        if (isColumnGroup(column)) {
          maxDepth = Math.max(maxDepth, maxLevel(column.children, level + 1));
        }
      });
      return maxDepth;
    };

    const fillHeaderRows = (columns: (ColumnType<T> | ColumnGroupType<T>)[], level = 0): void => {
      headerRows[level] = headerRows[level] || [];

      columns.forEach((column) => {
        if (isColumnGroup(column)) {
          const colSpan = getLeafColumns([column]).length;
          const groupCell: HeaderCell<T> = {
            title: column.title,
            dataIndex: '',
            key: column.key,
            width: column.width,
            minWidth: column.minWidth,
            align: column.align,
            className: column.className,
            colSpan,
            onHeaderCell: column.onHeaderCell
          };

          headerRows[level].push(groupCell);
          fillHeaderRows(column.children, level + 1);
        } else {
          const rowSpan = maxDepth - level;
          const cell: HeaderCell<T> = {
            ...column,
            rowSpan: rowSpan > 1 ? rowSpan : undefined
          };
          headerRows[level].push(cell);
        }
      });
    };

    const maxDepth = maxLevel(columns) + 1;
    fillHeaderRows(columns);

    return headerRows;
  };

  const selectionColumn: ColumnType<T> | null = useMemo(
    () =>
      rowSelection
        ? {
            title: rowSelection.hideSelectAll
              ? null
              : rowSelection.columnTitle || (
                  <BasicCheckbox
                    checked={_dataSource.length > 0 && selectedKeys.length === _dataSource.length}
                    // indeterminate={
                    //   selectedKeys.length > 0 && selectedKeys.length < _dataSource.length
                    // }
                    height={32}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    disabled={_dataSource.length === 0}
                  />
                ),
            dataIndex: 'selection',
            width: rowSelection.hideSelect ? 0 : rowSelection.columnWidth || 32,
            className: 'selection-column',
            fixed: rowSelection?.fixed,
            render: (_, record) => {
              const checkboxProps = rowSelection.getCheckboxProps?.(record) || {};
              const checked = selectedKeys.includes(record.key as string | number);

              return rowSelection.hideSelect ? null : rowSelection.type === 'radio' ? (
                <Radio
                  {...checkboxProps}
                  checked={checked}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => handleRowSelection(record)}
                />
              ) : (
                <BasicCheckbox
                  {...checkboxProps}
                  checked={checked}
                  height={32}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => handleRowSelection(record)}
                />
              );
            }
          }
        : null,
    [_dataSource.length, handleRowSelection, handleSelectAll, rowSelection, selectedKeys]
  );

  const allColumns = useMemo(() => {
    const indexColumn: ColumnType<T> | null = !noIndex
      ? {
          title: 'No',
          dataIndex: 'no',
          key: 'no',
          width: 120,
          render: (_, __, index) => index + 1
        }
      : null;

    const result = [];

    if (selectionColumn) {
      result.push(selectionColumn);
    }

    if (indexColumn) {
      result.push(indexColumn);
    }

    if (Array.isArray(columns)) {
      result.push(...columns);
    }

    return result;
  }, [columns, selectionColumn, noIndex]);

  const headerRows = getHeaderRows(allColumns);
  const leafColumns = getLeafColumns(allColumns);

  const sortDataSource = useMemo(() => getSortedData(), [getSortedData]);

  const groupedData = useMemo(() => {
    if (!rowGrouping?.groupBy) {
      return sortDataSource.map((item) => ({ ...item, __isGroupHeader: false }));
    }

    const groups: Record<string | number, T[]> = {};
    sortDataSource.forEach((record) => {
      const groupKey = rowGrouping.groupBy(record);
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(record);
    });

    const result: Array<T & { __isGroupHeader?: boolean; __groupKey?: string | number }> = [];

    Object.entries(groups).forEach(([key, groupRecords]) => {
      const headerRecord = {
        key: `group-header-${key}`,
        __isGroupHeader: true,
        __groupKey: key,
        __groupData: groupRecords
      } as unknown as T & {
        __isGroupHeader: boolean;
        __groupKey: string | number;
        __groupData: T[];
      };

      result.push(headerRecord);

      if (expandedGroups.has(key)) {
        groupRecords.forEach((record) => {
          result.push({ ...record, __groupKey: key });
        });
      }
    });

    return result;
  }, [sortDataSource, rowGrouping, expandedGroups]);

  return (
    <StyledTableWrapper className={rootClassName}>
      {!noTotal && <span className="total">[Total: {_dataSource.length}]</span>}

      <StyledTableContainer component={Paper} className={className}>
        <StyledTable stickyHeader>
          <StyledTableHead
            className={headerClassName}
            style={{ position: className === 'sub-table' ? 'static' : 'sticky', top: 0 }}
          >
            {headerRows.map((headerRow, rowIndex) => (
              <TableRow key={rowIndex} {...onHeaderRow?.(leafColumns, rowIndex)}>
                {headerRow.map((column, columnIndex) => {
                  const headerCellProps = column.onHeaderCell
                    ? column.onHeaderCell(column as ColumnType<T>)
                    : {};

                  return (
                    <StyledHeaderCell
                      key={
                        column.key || String('dataIndex' in column ? column.dataIndex : columnIndex)
                      }
                      align={column?.align || 'center'}
                      style={{
                        width: `${column.width} !important`,
                        minWidth: `${column.minWidth} !important`
                      }}
                      $width={column.width}
                      $minWidth={column.minWidth}
                      $sortable={!!(column as ColumnType<T>).sorter}
                      className={column.className}
                      colSpan={column.colSpan}
                      rowSpan={column.rowSpan}
                      {...headerCellProps}
                    >
                      {'sorter' in column ? (
                        <TableSortLabel
                          active={sortConfig.key === String(column.dataIndex)}
                          direction={sortConfig.direction === 'ascend' ? 'asc' : 'desc'}
                          onClick={() => handleSort(column as ColumnType<T>)}
                        >
                          {column.title}
                        </TableSortLabel>
                      ) : (
                        column.title
                      )}
                    </StyledHeaderCell>
                  );
                })}
              </TableRow>
            ))}
          </StyledTableHead>
          {groupedData && groupedData.length > 0 && (
            <TableBody className={bodyClassName} ref={combinedRef}>
              {groupedData.map((record, rowIndex) => {
                if (record.__isGroupHeader) {
                  const groupKey = record.__groupKey as string | number;
                  const groupData = record.__groupData as T[];
                  const isExpanded = expandedGroups.has(groupKey);

                  const headerProps = rowGrouping?.headerProps?.(groupKey, groupData) || {};

                  return (
                    <StyledTableRow
                      key={`group-header-${groupKey}`}
                      className="group-header-row"
                      $isGroupHeader={true}
                      {...headerProps}
                    >
                      {leafColumns
                        .map((column, columnIndex) => {
                          if (columnIndex === (selectionColumn ? 1 : 0)) {
                            return (
                              <StyledTableCell
                                key={column.key || String(column.dataIndex) || columnIndex}
                                colSpan={leafColumns.length}
                                align="left"
                                className={`group-header-cell ${column.className || ''}`}
                              >
                                <div
                                  className="group-header-content"
                                  style={{ display: 'flex', alignItems: 'center' }}
                                  onClick={() =>
                                    rowGrouping?.expandable !== false &&
                                    toggleGroupExpansion(groupKey)
                                  }
                                >
                                  {rowGrouping?.expandable !== false && (
                                    <span
                                      className="expand-icon"
                                      style={{ cursor: 'pointer', marginRight: '8px' }}
                                    >
                                      {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                                    </span>
                                  )}

                                  {rowGrouping?.renderGroupHeader ? (
                                    rowGrouping.renderGroupHeader(groupKey, groupData, rowIndex)
                                  ) : (
                                    <strong>
                                      {groupKey} ({groupData.length})
                                    </strong>
                                  )}
                                </div>
                              </StyledTableCell>
                            );
                          }

                          return null;
                        })
                        .filter(Boolean)}
                    </StyledTableRow>
                  );
                }

                const rowProps = getRowProps(record, rowIndex);
                const isGrouped = record.__groupKey !== undefined;

                return (
                  <StyledTableRow
                    key={record.key}
                    $isSelected={selectedKeys.includes(record.key as string | number)}
                    {...rowProps}
                  >
                    {leafColumns.map((column, columnIndex) => (
                      <StyledTableCell
                        key={column.key || String(column.dataIndex) || columnIndex}
                        className={column.className}
                        style={{
                          ...(columnIndex === (selectionColumn ? 1 : 0) && isGrouped
                            ? { paddingLeft: `${(rowGrouping?.indentSize || 24) + 16}px` }
                            : {})
                        }}
                        {...getCellProps(column, record, rowIndex)}
                      >
                        {getCellContent(record, column, rowIndex)}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                );
              })}
            </TableBody>
          )}
        </StyledTable>

        {groupedData && groupedData.length === 0 && (
          <div className="empty-box">
            <div>No data</div>
          </div>
        )}

        {rowRef && (
          <div
            ref={rowRef}
            style={{
              visibility: 'hidden',
              height: '10px',
              pointerEvents: 'none'
            }}
          />
        )}
      </StyledTableContainer>

      {!noPagenation && (
        <Pagination
          isAbsoluteToBottom={isAbsoluteToBottom}
          pagination={pagination}
          onChangePageSize={onChangePageSize}
        />
      )}
    </StyledTableWrapper>
  );
};

export const BasicTable = React.forwardRef(BasicTableBase) as BasicTableComponent;
BasicTable.displayName = 'BasicTable';
